import type {
  ConversationLifecycleState,
  DurableConversation,
} from "@/api/conversations";
import { translate } from "@/i18n";
import { inputBarPickersMessages } from "@/i18n/messages/input-bar-pickers";
import type en from "@/i18n/messages/input-bar-pickers/en";

type PickerKey = keyof typeof en;

type ConversationAction = keyof ConversationLifecycleState["actions"];

const M = inputBarPickersMessages;

const LABEL_KEYS = {
  open: "statusOpen",
  archived: "statusArchived",
} as const satisfies Record<ConversationLifecycleState["lifecycle"], PickerKey>;
const EXECUTION_LABEL_KEYS = {
  queued: "statusQueued",
  running: "statusRunning",
  completed: "statusOpen",
  failed: "statusTurnFailed",
  cancelled: "statusTurnCancelled",
  deferred: "statusDeferred",
  waiting_approval: "statusWaitingForApproval",
  paused: "statusPaused",
} as const satisfies Record<
  NonNullable<ConversationLifecycleState["execution"]>["status"],
  PickerKey
>;

/** Compact native worker status for list rows: terminal and waiting states read short. */
export function workerStatusLabel(status: string): string | null {
  if (status === "completed") return translate(M, "statusDone");
  if (status === "failed") return translate(M, "statusFailed");
  if (status === "cancelled") return translate(M, "statusStopped");
  if (status === "waiting_approval") return translate(M, "statusWaiting");
  return Object.prototype.hasOwnProperty.call(EXECUTION_LABEL_KEYS, status)
    ? translate(
        M,
        EXECUTION_LABEL_KEYS[status as keyof typeof EXECUTION_LABEL_KEYS],
      )
    : null;
}

export function conversationStatusLabel(chat: DurableConversation): string {
  if (chat.lifecycle) {
    if (chat.lifecycle.lifecycle !== "open") {
      const key = LABEL_KEYS[chat.lifecycle.lifecycle];
      return translate(M, key ?? "statusUnavailable");
    }
    return translate(
      M,
      chat.lifecycle.execution
        ? EXECUTION_LABEL_KEYS[chat.lifecycle.execution.status]
        : LABEL_KEYS.open,
    );
  }
  return translate(M, chat.archived_at ? "statusArchived" : "statusOpen");
}

export function conversationActionAllowed(
  chat: DurableConversation,
  action: ConversationAction,
  legacyFallback: boolean,
): boolean {
  return chat.lifecycle?.actions?.[action]?.allowed ?? legacyFallback;
}

export function conversationIsRunning(chat: DurableConversation): boolean {
  const execution = chat.lifecycle?.execution;
  return Boolean(
    execution?.is_active &&
    !["completed", "failed", "cancelled", "paused"].includes(execution.status),
  );
}

export function conversationRunningExecutionIds(
  chat: DurableConversation,
): ReadonlySet<string> {
  return conversationIsRunning(chat) && chat.lifecycle?.execution
    ? new Set([chat.lifecycle.execution.id])
    : new Set();
}
